import { Fragment, useState } from "react";
import { Button, Accordion, AccordionHeader, AccordionBody } from "@material-tailwind/react";
import { FilterFunction } from './FilterFunction';
//import DebouncedInput from "./DeBounce";
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
} from "@tanstack/react-table";

export const ResponsiveNirfTable = ({ data, columns }) => {
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      columnFilters,
      sorting: sorting,
    },
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
  });

  const buttonCSS = "m-2 p-2 h-10 w-fit \
                      bg-transparent text-primary-900 hover:text-white hover:bg-primary-900 \
                      border-2 rounded-lg \
                      text-base font-bold tracking-widest normal-case items-center"

  const mobileButtonCSS = "m-1 p-2 h-9 w-full sm:w-auto \
                            bg-transparent text-primary-900 hover:text-white hover:bg-primary-900 \
                            border-2 rounded-lg \
                            text-xs sm:text-sm font-semibold normal-case"

  // Color scheme for cards - using brand color #19BA92 (teal/green) and complementary colors
  const cardColors = [
    {
        bg: "bg-gradient-to-br from-primary-100 to-green-50",
        border: "border-primary-300",
        accent: "bg-primary-200",
        text: "text-primary-900"
    },
    {
      bg: "bg-gradient-to-br from-gray-50 to-emerald-50",
      border: "border-emerald-300",
      accent: "bg-emerald-200",
      text: "text-emerald-900"
    },
  ];

  function getCardColors(index) {
    return cardColors[index % cardColors.length];
  }

  const clearAllFilters = () => {
    setColumnFilters([]);
  };

  const getSortIcon = (column) => {
    const sortState = column.getIsSorted();
    if (sortState === 'asc') return '🔼';
    if (sortState === 'desc') return '🔽';
    return '⇅';
  };

  // Mobile Card View
  const renderMobileCard = (row, index) => {
    const colors = getCardColors(index);
    const cells = row.getVisibleCells();
    const headerGroups = table.getHeaderGroups();

    // Helper function to find cell by column ID
    const findCell = (columnId) => {
      return cells.find(cell => cell.column.id === columnId);
    };

    // Helper function to get header text
    const getHeaderText = (columnId) => {
      for (const headerGroup of headerGroups) {
        const header = headerGroup.headers.find(h => h.column.id === columnId);
        if (header) {
          return flexRender(header.column.columnDef.header, header.getContext());
        }
      }
      return '';
    };

    // Helper function to render a field
    const renderField = (columnId, isBold = false, isLarge = false, showBorder = true) => {
      const cell = findCell(columnId);
      if (!cell) return null;

      const headerText = getHeaderText(columnId);
      const isInstituteName = columnId === 'i001_inst_name';

      return (
        <div key={cell.id} className={showBorder ? `border-b ${colors.border} pb-2 mb-2` : ''}>
          {!isInstituteName && (
            <div className={`text-xs font-semibold ${colors.text} mb-1 uppercase tracking-wide`}>
              {headerText}
            </div>
          )}
          <div className={`${isInstituteName || isLarge ? `text-lg font-bold ${colors.text}` : isBold ? `text-sm font-bold ${colors.text}` : `text-sm ${colors.text} font-medium`}`}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </div>
        </div>
      );
    };

    return (
      <div key={row.id} className={`${colors.bg} border-2 ${colors.border} rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-4 space-y-2 mb-4`}>
        {/* First Row: Academic Year, Rank, Score */}
        <div className={`flex flex-wrap gap-3 border-b ${colors.border} pb-2 mb-2`}>
          <div className="flex-1 min-w-[100px]">
            {renderField('i001_inst_year', false, false, false)}
          </div>
          <div className="flex-1 min-w-[100px]">
            {renderField('i001_inst_rank', false, false, false)}
          </div>
          <div className="flex-1 min-w-[100px]">
            {renderField('i001_inst_score', false, false, false)}
          </div>
        </div>
        
        {/* Second Row: Institute Name */}
        {renderField('i001_inst_name', false, true, true)}
        
        {/* Third Row: City and State */}
        <div className={`flex flex-wrap gap-3 pt-2`}>
          <div className="flex-1 min-w-[120px]">
            {renderField('i001_inst_city', false, false, false)}
          </div>
          <div className="flex-1 min-w-[120px]">
            {renderField('i001_inst_state', false, false, false)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Fragment>
      <div className="w-full p-3 sm:p-4 md:p-5">
        {/* Mobile: Collapsible Filters */}
        <div className="md:hidden mb-4">
          <Accordion open={filterOpen} icon={<Icon id="filters" open={filterOpen} />}>
            <AccordionHeader
              onClick={() => setFilterOpen(!filterOpen)}
              className="border-2 border-primary-500 rounded-lg shadow-sm pr-4 py-2"
            >
              <div className="flex items-center justify-between w-full ml-2">
                <span className="text-base font-semibold text-primary-900">Filters</span>
                {columnFilters.length > 0 && (
                  <span className="text-xs bg-primary-500 text-white rounded-full px-2 py-0.5">
                    {columnFilters.length}
                  </span>
                )}
              </div>
            </AccordionHeader>
            <AccordionBody>
              <div className="space-y-3 pt-2">
                {table.getHeaderGroups()[0].headers.map((header) => {
                  if (header.column.getCanFilter()) {
                    return (
                      <div key={header.id} className="border-b border-gray-200 pb-3 last:border-b-0">
                        <div className="text-xs font-semibold text-gray-700 mb-2">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </div>
                        <FilterFunction column={header.column} table={table} />
                      </div>
                    );
                  }
                  return null;
                })}
                {columnFilters.length > 0 && (
                  <Button
                    onClick={clearAllFilters}
                    className={`${mobileButtonCSS} w-full mt-2`}
                  >
                    Clear All Filters
                  </Button>
                )}
              </div>
            </AccordionBody>
          </Accordion>
        </div>

        {/* Mobile: Sort Controls */}
        <div className="md:hidden mb-4">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs font-semibold text-gray-700">Sort by:</span>
            {table.getHeaderGroups()[0].headers
              .filter(header => header.column.getCanSort())
              .map((header) => (
                <button
                  key={header.id}
                  onClick={header.column.getToggleSortingHandler()}
                  className={`px-3 py-1.5 text-xs rounded-lg border-2 transition-colors ${
                    header.column.getIsSorted()
                      ? 'bg-primary-500 text-white border-primary-500'
                      : 'bg-white text-primary-900 border-primary-500 hover:bg-primary-50'
                  }`}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  <span className="ml-1">{getSortIcon(header.column)}</span>
                </button>
              ))}
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden">
          {table.getRowModel().rows.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No results found
            </div>
          ) : (
            table.getRowModel().rows.map((row, index) => renderMobileCard(row, index))
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block min-w-full overflow-x-auto">
          <div className="border-2 border-primary-500 rounded-lg overflow-hidden">
            <table className="min-w-full">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          className="bg-primary-500 text-white text-sm border-[1px] border-gray-400 font-nunito font-semibold p-2"
                        >
                          {header.isPlaceholder ? null : (
                            <>
                              <div
                                {...{
                                  className: header.column.getCanSort() ? "cursor-pointer select-none" : "",
                                  onClick: header.column.getToggleSortingHandler(),
                                }}
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {getSortIcon(header.column) !== '⇅' && (
                                  <span className="ml-1">{getSortIcon(header.column)}</span>
                                )}
                              </div>
                              {header.column.getCanFilter() ? (
                                <div className="mt-2">
                                  <FilterFunction column={header.column} table={table} />
                                </div>
                              ) : null}
                            </>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="text-center py-8 text-gray-500 border-2 border-gray-100">
                      No results found
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="even:bg-primary-100 odd:bg-white hover:bg-primary-200 transition-colors">
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          style={{ width: cell.column.getSize() }}
                          className="p-2 border-2 border-gray-100 text-secondary-900 text-sm font-nunito"
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination - Mobile */}
        <div className="md:hidden mt-4">
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-center text-xs text-gray-600 mb-2">
              <span>
                Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
              </span>
              <span>
                Showing {table.getRowModel().rows.length} of {table.getFilteredRowModel().rows.length} results
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                className={`${mobileButtonCSS}`}
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
              >
                First
              </Button>
              <Button
                className={`${mobileButtonCSS}`}
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                Previous
              </Button>
              <Button
                className={`${mobileButtonCSS}`}
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Next
              </Button>
              <Button
                className={`${mobileButtonCSS}`}
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
              >
                Last
              </Button>
            </div>
          </div>
        </div>

        {/* Pagination - Desktop */}
        <div className="hidden md:flex items-center justify-center gap-2 mt-4 flex-wrap">
          <div className="text-sm text-gray-600 mr-4">
            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()} 
            {' '}({table.getRowModel().rows.length} of {table.getFilteredRowModel().rows.length} results)
          </div>
          <Button
            className={`${buttonCSS}`}
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            First Page
          </Button>
          <Button
            className={`${buttonCSS}`}
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.previousPage()}
          >
            {"<<"}
          </Button>
          <Button
            className={`${buttonCSS}`}
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            {">>"}
          </Button>
          <Button
            className={`${buttonCSS}`}
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            Last Page
          </Button>
        </div>
      </div>
    </Fragment>
  );
};

// Icon component for Accordion
function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

